import type { LucideIcon } from "lucide-react";
import type { InsightSourceRecord } from "@graftus/schema";

import type { BusinessData } from "@/types/businessData";

export type NudgeSeverity = "info" | "warning" | "danger";

export type NudgeAccent = "primary" | "warning" | "danger" | "info";

export type NudgeView = {
  icon: LucideIcon;
  severity: NudgeSeverity;
  accent: NudgeAccent;
  title: string;
  description: string;
  progress?: { done: number; total: number };
  /**
   * Primary destination link. Omitted for AI insights, whose single action is
   * the `suggestedPrompt` handoff into Intelligence (rendered as the primary).
   */
  cta?: { label: string; to: string };
  /** When "button", renders filled button CTAs + left-border accent (signal style). */
  ctaVariant?: "link" | "button";
  /** Optional second CTA button, only rendered when ctaVariant === "button". */
  secondaryCta?: { label: string; to: string };
  /** Short label (≤ 30 chars) to surface this insight as a dashboard starter chip. */
  starterLabel?: string;
  /**
   * Optional prompt that hands this insight to the Intelligence agent. Surfaced
   * as an "Ask AI about this" action on the dashboard insights strip when the
   * business has the intelligence entitlement.
   */
  suggestedPrompt?: string;
  /**
   * True for owner-visible insights derived from the viewer's own connected
   * accounts (calendar, inbox) — rendered with a "Personlig" badge so it's
   * clear colleagues can't see them.
   */
  personal?: boolean;
  /** Composio toolkit slugs the insight was derived from (attribution). */
  sourceToolkits?: string[];
  /**
   * Vault documents the insight was grounded in. Shown in the expanded card so
   * a claim about "how we work" can be traced back to the document it came from.
   */
  sourceDocuments?: { id: string; title: string }[];
  /** Supporting bullet points, revealed when the card is expanded. */
  evidence?: string[];
  /**
   * When the underlying event happened (e.g. call time), distinct from when
   * the insight was generated. Timelines anchor on this when present.
   */
  occurredAt?: string;
  /** External records (e.g. recorded calls) the insight was derived from. */
  sourceRecords?: InsightSourceRecord[];
};

export type PulseNudgeContext = {
  show: boolean;
  scoredCount: number;
  totalMembers: number;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
};

export type PulseSetupNudgeContext = {
  show: boolean;
};

export type KpiSetupNudgeContext = {
  show: boolean;
};

export type NudgeContext = {
  businessProfile: BusinessData | null;
  /** Undefined while `recentSubmittedLeads` is loading when leads access is enabled. */
  pendingSubmittedLeadsCount: number | undefined;
  submittedLeadsLoading: boolean;
  pulseNudge?: PulseNudgeContext | null;
  pulseSetupNudge?: PulseSetupNudgeContext | null;
  kpiSetupNudge?: KpiSetupNudgeContext | null;
  pulseNudgeLoading?: boolean;
};

export type Nudge = {
  id: string;
  priority: number;
  evaluate: (ctx: NudgeContext) => NudgeView | null;
};

export function nudgeDismissStorageKey(id: string, periodStart?: string): string {
  if (periodStart) {
    return `graftus_nudge_dismissed:${id}:${periodStart}`;
  }
  return `graftus_nudge_dismissed:${id}`;
}

export function pulseChatNudgeDismissKey(periodStart: string): string {
  return `graftus_pulse_chat_nudge:${periodStart}`;
}

export function pulseChatSetupNudgeDismissKey(): string {
  return "graftus_pulse_chat_nudge:setup";
}
